import { Badge, Button, IconButton, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { useNotifications } from '../../../contexts/NotificationContext'
import { formatDateTime, useAppToast } from '../../../helpers'
import { INotification } from '../../../services/Notification/interfaces'
import { NotificationService } from '../../../services/Notification/NotificationService'

function sanitizeIconClass(icon: string): string {
  return icon.replace(/[^a-zA-Z0-9-]/g, '')
}

function isValidLink(link: string, linkType: 'internal' | 'external'): boolean {
  if (linkType === 'internal') return link.startsWith('/')
  return /^https?:\/\//i.test(link)
}

export default function NotificationsPage() {
  const toast = useAppToast()
  const navigate = useNavigate()
  const notificationService = useRef(new NotificationService()).current
  const { markAllAsRead: markAllAsReadContext } = useNotifications()

  const [notifications, setNotifications] = useState<INotification[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const loadNotifications = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await notificationService.list(page, pageSize)
      setNotifications(data.notifications)
      setTotalPages(Math.ceil(data.total_filtered / data.page_size))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as notificações', error)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, notificationService, toast])

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  const handleMarkAsRead = async (id: number) => {
    try {
      await notificationService.markAsRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      )
    } catch {
      toast.error('Não foi possível marcar a notificação como lida')
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadContext()
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
    } catch {
      toast.error('Não foi possível marcar todas como lidas')
    }
  }

  const handleNavigate = async (notification: INotification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id)
    }

    if (!isValidLink(notification.link, notification.link_type)) return

    if (notification.link_type === 'external') {
      window.open(notification.link, '_blank', 'noopener,noreferrer')
    } else {
      navigate(notification.link)
    }
  }

  return (
    <>
      <PageTitle
        title="Notificações"
        description="Avisos do painel enviados para você"
        mb={4}
        actions={
          <Button
            leftIcon={<Icon name="lni lni-checkmark" />}
            colorScheme="magenta"
            size="sm"
            onClick={handleMarkAllAsRead}
          >
            Marcar todas como lidas
          </Button>
        }
      />

      <TableContainer mt={2}>
        <Table>
          <Thead>
            <Tr>
              <Th w="40px"></Th>
              <Th>Título</Th>
              <Th>Descrição</Th>
              <Th>Data</Th>
              <Th>Status</Th>
              <Th w="80px">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : loadError ? (
              <TableErrorRow colSpan={6} entity="notificações" feminine onRetry={loadNotifications} />
            ) : notifications.length === 0 ? (
              <TableEmptyRow colSpan={6} entity="notificação" feminine />
            ) : (
              notifications.map((notification) => (
                <Tr
                  key={notification.id}
                  role="button"
                  tabIndex={0}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  bg={notification.is_read ? 'transparent' : 'blue.50'}
                  onClick={() => handleNavigate(notification)}
                  onKeyDown={(event) => {
                    // A linha só responde quando ela mesma está focada: o botão "marcar como
                    // lida" é um filho focável, e sem isto o Enter dele subia até aqui, tinha o
                    // clique cancelado pelo preventDefault e ainda navegava para a notificação.
                    if (event.target !== event.currentTarget) return

                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleNavigate(notification)
                    }
                  }}
                >
                  <Td textAlign="center">
                    <Icon name={`lni lni-${sanitizeIconClass(notification.icon)}`} />
                  </Td>
                  <Td>
                    <Text fontWeight={notification.is_read ? 'normal' : 'bold'} noOfLines={1} maxW="250px">
                      {notification.title}
                    </Text>
                  </Td>
                  <Td>
                    <Text noOfLines={1} maxW="300px" color="gray.600">
                      {notification.description}
                    </Text>
                  </Td>
                  <Td whiteSpace="nowrap">{formatDateTime(notification.created_at)}</Td>
                  <Td>
                    <Badge colorScheme={notification.is_read ? 'green' : 'yellow'}>
                      {notification.is_read ? 'Lida' : 'Não lida'}
                    </Badge>
                  </Td>
                  <Td>
                    {!notification.is_read && (
                      <Tooltip label="Marcar como lida">
                        <IconButton
                          aria-label="Marcar como lida"
                          icon={<Icon name="lni lni-eye" />}
                          size="xs"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMarkAsRead(notification.id)
                          }}
                        />
                      </Tooltip>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p: number) => setPage(p)}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />
    </>
  )
}
